/**
 * OutcomesGrid - Consumer Duty outcomes 2x2 grid
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../../../../ui/card';
import { Skeleton } from '../../../../../ui/skeleton';
import { cn } from '../../../../../../lib/utils';
import { OutcomeCard } from '../molecules';
import type { OutcomesGridProps } from '../../types';

export function OutcomesGrid({
  data,
  onCauseClick,
  isLoading,
  isError,
  className,
}: OutcomesGridProps) {
  if (isError) {
    return (
      <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)}>
        <CardHeader>
          <CardTitle>Consumer Duty Outcomes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center text-destructive">
            Failed to load outcomes data
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)} aria-busy="true">
        <CardHeader>
          <Skeleton className="h-6 w-64" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-64 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.outcomes.length === 0) {
    return (
      <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)}>
        <CardHeader>
          <CardTitle>Consumer Duty Outcomes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center text-muted-foreground">
            No outcome data found for the selected filters
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn('space-y-4', className)} data-testid="outcomes-grid">
      <div>
        <h2 className="font-jakarta text-xl font-bold tracking-[-0.02em] text-foreground">
          Consumer Duty Outcomes
        </h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Root cause breakdown by regulatory outcome
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {data.outcomes.map((outcome) => (
          <OutcomeCard
            key={outcome.id}
            outcome={outcome}
            onCauseClick={onCauseClick}
          />
        ))}
      </div>
    </div>
  );
}
